import { useState } from 'react'
import { T, CAT_LABELS, catColor } from '../lib/tokens'
import { useSearch, useCategories } from '../lib/hooks'
import { Sidebar } from '../components/Sidebar'
import { Topbar } from '../components/Topbar'
import { LoadingSpinner, CatBadge, TimeAgo } from '../components/ui'

export function SearchPage() {
  const [query, setQuery] = useState('')
  const [submitted, setSubmitted] = useState('')
  const [catFilter, setCatFilter] = useState<string | null>(null)
  const { data: results = [], isLoading } = useSearch(submitted)
  const { data: categories = [] } = useCategories()

  const filtered = catFilter ? results.filter(i => i.cat_id === catFilter) : results

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitted(query.trim())
  }

  return (
    <div style={{ display:'flex',height:'100dvh',background:T.bg,color:T.text,fontFamily:T.font,overflow:'hidden' }}>
      <Sidebar />
      <div style={{ flex:1,display:'flex',flexDirection:'column',overflow:'hidden' }}>
        <Topbar />
        <main style={{ flex:1,overflowY:'auto',padding:'28px 28px 40px' }}>
          <h2 style={{ fontSize:18,fontWeight:700,color:T.text,margin:'0 0 20px' }}>検索</h2>

          {/* 検索フォーム */}
          <form onSubmit={handleSubmit} style={{ display:'flex',gap:10,marginBottom:20 }}>
            <div style={{ flex:1,display:'flex',alignItems:'center',gap:8,background:T.bgRaised,border:`1px solid ${T.borderHi}`,borderRadius:7,padding:'0 12px',height:40 }}>
              <span style={{ fontSize:14,color:T.textMute }}>⌕</span>
              <input
                value={query}
                onChange={e=>setQuery(e.target.value)}
                placeholder="キーワード・URL・タグ…"
                style={{ flex:1,background:'transparent',border:'none',outline:'none',color:T.text,fontSize:14,fontFamily:T.font }}
                autoFocus
              />
            </div>
            <button type="submit" style={{ padding:'0 20px',background:T.accent,border:'none',borderRadius:7,color:'#0a0b0e',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:T.font }}>検索</button>
          </form>

          {/* カテゴリフィルタ */}
          {results.length > 0 && (
            <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginBottom:20 }}>
              <FilterPill label="すべて" active={catFilter===null} onClick={()=>setCatFilter(null)} />
              {categories.filter(c=>results.some(r=>r.cat_id===c.id)).map(cat=>(
                <FilterPill key={cat.id} label={cat.label} catId={cat.id} active={catFilter===cat.id} onClick={()=>setCatFilter(catFilter===cat.id?null:cat.id)} />
              ))}
            </div>
          )}

          {/* ローディング */}
          {isLoading && <div style={{ padding:'20px 0' }}><LoadingSpinner /></div>}

          {/* 結果なし */}
          {submitted && !isLoading && results.length === 0 && (
            <div style={{ padding:'48px 0',textAlign:'center',color:T.textMute,fontSize:13 }}>
              「{submitted}」に一致するアイテムが見つかりません
            </div>
          )}

          {/* 結果 */}
          {filtered.length > 0 && (
            <>
              <div style={{ fontSize:12,color:T.textMute,marginBottom:14,fontFamily:T.fontMono }}>
                {filtered.length} 件{catFilter?` · ${CAT_LABELS[catFilter]??catFilter}`:''}
              </div>
              <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
                {filtered.map(item => <SearchResultRow key={item.id} item={item} query={submitted} />)}
              </div>
            </>
          )}

          {/* 未検索時 */}
          {!submitted && (
            <div style={{ padding:'60px 0',textAlign:'center',color:T.textMute }}>
              <div style={{ fontSize:28,marginBottom:12 }}>⌕</div>
              <div style={{ fontSize:13 }}>キーワードを入力して検索してください</div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

function SearchResultRow({ item, query }: { item: any; query: string }) {
  function highlight(text: string) {
    if (!query) return text
    const idx = text.toLowerCase().indexOf(query.toLowerCase())
    if (idx === -1) return text.slice(0, 120)
    const start = Math.max(0, idx - 30)
    const end = Math.min(text.length, idx + query.length + 80)
    const before = text.slice(start, idx)
    const match = text.slice(idx, idx + query.length)
    const after = text.slice(idx + query.length, end)
    return { before: (start > 0 ? '…' : '') + before, match, after: after + (end < text.length ? '…' : '') }
  }

  const summaryHl = item.summary ? highlight(item.summary) : null

  return (
    <a href={`/items/${item.id}`} style={{ textDecoration:'none' }}>
      <div style={{ background:T.bgRaised,border:`1px solid ${T.border}`,borderRadius:8,padding:'12px 16px',display:'flex',flexDirection:'column',gap:7,cursor:'pointer',transition:'border-color 0.12s' }}
        onMouseEnter={e=>(e.currentTarget.style.borderColor=T.borderHi)}
        onMouseLeave={e=>(e.currentTarget.style.borderColor=T.border)}
      >
        <div style={{ display:'flex',alignItems:'center',gap:8,flexWrap:'wrap' }}>
          <CatBadge catId={item.cat_id} />
          <span style={{ fontSize:10,fontFamily:T.fontMono,color:T.textMute }}>{item.kind}</span>
          {item.source_url && <span style={{ fontSize:10,fontFamily:T.fontMono,color:T.textMute }}>{(()=>{try{return new URL(item.source_url).hostname.replace('www.','')}catch{return ''}})()}</span>}
          <TimeAgo iso={item.created_at} />
        </div>
        <div style={{ fontSize:14,fontWeight:600,color:T.text }}>{item.title??item.original_content.slice(0,80)}</div>
        {summaryHl && (
          <div style={{ fontSize:12,color:T.textDim,lineHeight:1.6 }}>
            {typeof summaryHl === 'string' ? summaryHl : (
              <>{summaryHl.before}<mark style={{ background:'oklch(0.72 0.16 258/0.25)',color:T.text,padding:'0 1px',borderRadius:2 }}>{summaryHl.match}</mark>{summaryHl.after}</>
            )}
          </div>
        )}
      </div>
    </a>
  )
}

function FilterPill({ label, catId, active, onClick }: { label:string;catId?:string;active:boolean;onClick:()=>void }) {
  return (
    <button onClick={onClick} style={{ padding:'4px 10px',borderRadius:5,background:active?T.bgActive:'transparent',border:`1px solid ${active?T.borderHi:T.border}`,color:active?T.text:T.textDim,fontSize:12,cursor:'pointer',fontFamily:T.font,display:'flex',alignItems:'center',gap:5 }}>
      {catId&&<span style={{ width:5,height:5,borderRadius:1,background:catColor(catId),display:'inline-block' }} />}
      {label}
    </button>
  )
}
